import { Controller } from "@hotwired/stimulus"
import { hide, show, getUrlAnchor, scrollToAnchor } from "../lib/helpers"
import { log } from "../lib/log"
import { StoredSetting } from "../lib/stored_setting"

const EXPAND = "expand"
const COLLAPSE = "collapse"
const EXPANDED_OR_COLLAPSED_KEY = "expanded-or-collapsed-items"
const CONTROL_DEFAULTS_KEY = "expanded-or-collapsed-control-default-items"

const eagerLoadChildTurboFrames = (parentElement) => {
  parentElement
    .querySelectorAll("turbo-frame[loading='lazy']")
    .forEach(tf => {
      if (tf.closest("[data-controller~='expand-or-collapse']") == parentElement) {
        tf.setAttribute("loading", "eager")
      }
    })
}

export default class extends Controller {
  static targets = ["hideWhenExpanded", "hideWhenCollapsed"]
  static values = {
    identifier: String,
    controlDefault: String,
    eagerLoadWait: {
      type: Number,
      default: 150
    }
  }

  disconnect() {
    document.removeEventListener("turbo:morph", this.morphHandler)
    log("expandOrCollapse: disconnect")
  }

  connect() {
    log("expandOrCollapse: connect")

    if (!this.hasHideWhenExpandedTarget || !this.hasHideWhenCollapsedTarget) {
      log("Expand or Collapse Controller: Should have at least one hideWhenExpandedTargets and one hideWhenCollapsedTargets", this.identifierValue)
    }

    // Reset stale remembered state BEFORE the anchor check, so an
    // anchor-driven EXPAND below isn't wiped by the reset.
    this.resetStoredStateIfDefaultChanged()

    // Expand if child element has anchor tag pointing to it
    const anchor = getUrlAnchor()
    if (anchor && this.element.querySelector(anchor)) {
      StoredSetting.set(EXPANDED_OR_COLLAPSED_KEY, this.identifierValue, EXPAND)
      this.expand()
      scrollToAnchor()
    }

    this.applyStoredState()

    // A Turbo page-refresh morph resets our targets to the server-rendered
    // (collapsed) markup without reconnecting this controller, so re-apply
    // the stored state after every morph.
    this.morphHandler = () => this.applyStoredState()
    document.addEventListener("turbo:morph", this.morphHandler)
  }

  // If the server-side default changed (possibly during a morph, e.g.
  // saving display_expanded_vision_on_load), reset the remembered state so
  // the new default wins, and persist the new default immediately.
  resetStoredStateIfDefaultChanged() {
    if (this.rememberedControlDefault !== this.controlDefaultValue) {
      StoredSetting.remove(CONTROL_DEFAULTS_KEY, this.identifierValue)
      StoredSetting.remove(EXPANDED_OR_COLLAPSED_KEY, this.identifierValue)
    }

    StoredSetting.set(CONTROL_DEFAULTS_KEY, this.identifierValue, this.controlDefaultValue)
  }

  applyStoredState() {
    this.resetStoredStateIfDefaultChanged()

    // When remembered as expanded
    if (this.isRememberedAsExpanded) {
      this.expand()
      return
    }

    // When remembered as collapsed
    if (this.isRememberedAsCollapsed) {
      this.collapse()
      return
    }

    // When relying on defaults
    if (this.isExpandingControlDefault) {
      this.expand()
    } else {
      this.collapse()
    }
  }

  get isExpandingControlDefault() {
    return !this.isCollapsingControlDefault
  }

  get isRememberedAsExpanded() {
    return StoredSetting.get(EXPANDED_OR_COLLAPSED_KEY, this.identifierValue) === EXPAND
  }

  get isCollapsingControlDefault() {
    return this.controlDefaultValue === COLLAPSE
  }

  get isRememberedAsCollapsed() {
    return StoredSetting.get(EXPANDED_OR_COLLAPSED_KEY, this.identifierValue) === COLLAPSE
  }

  get rememberedControlDefault() {
    return StoredSetting.get(CONTROL_DEFAULTS_KEY, this.identifierValue)
  }

  expand(event) {
    event?.stopImmediatePropagation()
    event?.preventDefault()

    eagerLoadChildTurboFrames(this.element)

    hide(this.hideWhenExpandedTargets)
    show(this.hideWhenCollapsedTargets)

    this.remember(EXPAND)
  }

  collapse(event) {
    event?.stopImmediatePropagation()
    event?.preventDefault()

    hide(this.hideWhenCollapsedTargets)
    show(this.hideWhenExpandedTargets)

    this.remember(COLLAPSE)
  }

  loadChildTurboFramesStart(event) {
    this.eagerLoadingTimer =
      setTimeout(
        () => {
          eagerLoadChildTurboFrames(this.element)
          this.eagerLoadingTimer = null
        },
        this.eagerLoadWaitValue
      )
  }

  loadChildTurboFramesEnd(event) {
    if (this.eagerLoadingTimer) {
      clearTimeout(this.eagerLoadingTimer)
      this.eagerLoadingTimer = null
    }
  }

  remember(value) {
    StoredSetting.set(EXPANDED_OR_COLLAPSED_KEY, this.identifierValue, value)
  }
}